import type { Config as AdapterConfig, OperationPresentationMode } from '@/workflow/_contract/config';
import type { Config, ModeConfig } from '@/workflow-runner/_contract/config';

function isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export function isNaturalResultSettlementOffsets(
    value: unknown,
): value is NonNullable<ModeConfig['camera']>['naturalResultSettlementOffsets'] {
    return isRecord(value) && ['addition', 'subtraction', 'multiplication', 'division'].every((operation) => {
        const offset = value[operation];
        return Array.isArray(offset) && offset.length === 3
            && offset.every((entry) => typeof entry === 'number' && Number.isFinite(entry))
            && Math.hypot(...offset) > 0;
    });
}

function hasNumber(record: Record<string, unknown>, key: string): boolean {
    return typeof record[key] === 'number';
}

function hasOptionalNumber(record: Record<string, unknown>, key: string): boolean {
    return record[key] === undefined || typeof record[key] === 'number';
}

function isNonActiveDimming(value: unknown): boolean {
    if (!isRecord(value)) return false;
    const booleanMapKeys = ['enabledByConstellationKey', 'enabledByLayoutKind'];
    const numberMapKeys = ['opacityByConstellationKey', 'opacityByLayoutKind'];
    return hasOptionalNumber(value, 'defaultOpacity')
        && booleanMapKeys.every((key) => value[key] === undefined
            || isRecord(value[key])
            && Object.values(value[key]).every((entry) => typeof entry === 'boolean'))
        && numberMapKeys.every((key) => value[key] === undefined
            || isRecord(value[key])
            && Object.values(value[key]).every((entry) => typeof entry === 'number'));
}

function isModeConfig(value: unknown): value is ModeConfig {
    if (!isRecord(value)) return false;
    if (value.showOperationCaption !== undefined && typeof value.showOperationCaption !== 'boolean') return false;
    const traceSelection = value.traceSelection;
    const playback = value.playback;
    const nonActiveDimming = value.nonActiveDimming;
    const camera = value.camera;
    if (!isRecord(traceSelection) || !isRecord(playback) || !isNonActiveDimming(nonActiveDimming)) return false;

    const loopAnimation = playback.loopContinuationAnimation;
    const branchAnimation = playback.branchExitRebindAnimation;
    const naturalTransport = playback.naturalLessThanTransport;
    const radialActivation = playback.resourceTypeRadialActivation;
    const ripple = playback.resourceTypeRadialRipple;
    if (typeof playback.inputPotentialPropagationDurationMs !== 'number'
        || !Number.isFinite(playback.inputPotentialPropagationDurationMs)
        || playback.inputPotentialPropagationDurationMs < 0) return false;
    if (!isRecord(ripple) || typeof ripple.enabled !== 'boolean'
        || typeof ripple.amplitude !== 'number' || !Number.isFinite(ripple.amplitude) || ripple.amplitude < 0
        || typeof ripple.pulseLength !== 'number' || !Number.isFinite(ripple.pulseLength) || ripple.pulseLength <= 0
        || typeof ripple.oscillationCount !== 'number' || !Number.isFinite(ripple.oscillationCount) || ripple.oscillationCount <= 0) return false;
    if (!isRecord(radialActivation)
        || !['inputDurationMs', 'processingHoldMs', 'postOutputHoldMs', 'outputDurationMs'].every((key) => {
            const duration = radialActivation[key];
            return typeof duration === 'number' && Number.isFinite(duration) && duration >= 0;
        })) return false;
    if (!isRecord(loopAnimation) || !isRecord(naturalTransport)) return false;
    if (branchAnimation !== undefined && !isRecord(branchAnimation)) return false;

    return (camera === undefined || isRecord(camera)
        && (camera.navigationMode === 'free' || camera.navigationMode === 'guided')
        && Array.isArray(camera.naturalLessThanInspectionOffset)
        && camera.naturalLessThanInspectionOffset.length === 3
        && camera.naturalLessThanInspectionOffset.every((value) => typeof value === 'number' && Number.isFinite(value))
        && isNaturalResultSettlementOffsets(camera.naturalResultSettlementOffsets)
        && typeof camera.panelsElevation === 'number' && Number.isFinite(camera.panelsElevation) && camera.panelsElevation > 0
        && ['foldedElevation', 'operationAngleDegrees', 'operationElevation', 'operationPadding', 'overviewPadding', 'fov', 'navigationDurationMs']
            .every((key) => hasNumber(camera, key)))
        && hasNumber(value, 'standoffNearFraction')
        && hasNumber(value, 'standoffFarFraction')
        && (traceSelection.workflowTraceSelectionKind === 'latest-by-timestamp'
            || traceSelection.workflowTraceSelectionKind === 'by-handle')
        && (traceSelection.workflowTraceHandle === undefined
            || typeof traceSelection.workflowTraceHandle === 'string')
        && hasOptionalNumber(traceSelection, 'replayPositionIndex')
        && typeof playback.positionIntervalMs === 'number'
        && typeof playback.startPlaying === 'boolean'
        && hasNumber(loopAnimation, 'transitionDurationMs')
        && hasOptionalNumber(loopAnimation, 'spaceShiftPhaseStart')
        && hasOptionalNumber(loopAnimation, 'spaceShiftPhaseEnd')
        && (branchAnimation === undefined
            || hasNumber(branchAnimation, 'transitionDurationMs')
            && hasOptionalNumber(branchAnimation, 'spaceShiftPhaseStart')
            && hasOptionalNumber(branchAnimation, 'spaceShiftPhaseEnd'))
        && hasNumber(naturalTransport, 'naturalRadialEntryDurationMs')
        && hasNumber(naturalTransport, 'naturalRingHingeDurationMs')
        && hasNumber(naturalTransport, 'toolStepRingTravelDurationMs')
        && hasNumber(naturalTransport, 'booleanRingHingeDurationMs')
        && hasNumber(naturalTransport, 'booleanRadialExitDurationMs')
        && hasNumber(naturalTransport, 'settlementDurationMs')
        && (playback.naturalLessThanPresentationMode === 'travelling-eye'
            || playback.naturalLessThanPresentationMode === 'boolean-channel')
        && (value.workflowStateEntries === undefined
            || isRecord(value.workflowStateEntries)
            && (value.workflowStateEntries.operationPresentationMode === undefined
                || value.workflowStateEntries.operationPresentationMode === 'panels'
                || value.workflowStateEntries.operationPresentationMode === 'wheels'));
}

function isConfig(config: AdapterConfig): config is Config {
    return config.adapterMode === 'runner' && isModeConfig(config.adapterModeConfig);
}

export function requireConfig(config: AdapterConfig): Config {
    if (!isConfig(config)) {
        throw new Error(`Expected Runner Workflow config, received '${config.adapterMode}'.`);
    }
    return config;
}

export function resolveOperationPresentationMode(
    config: Config,
): OperationPresentationMode {
    return config.adapterModeConfig.workflowStateEntries?.operationPresentationMode
        ?? config.domainPresentation?.entities?.workflowStateEntries?.operationPresentationMode
        ?? 'wheels';
}